import { orderBy, toNumber } from 'lodash';
import { SortOptions } from 'src/components/subHeader/SubHeaderWithSorting.interface';
import { DefaultQueryProps } from 'src/hooks';
import { IProduct } from 'src/interfaces';

import { filterProductByKeywords } from '../../listProductsByCategoryPage/modules/ListProductsByCategoryPage.helper';

const formatSortName = (sort: SortOptions) => {
  switch (sort) {
    case SortOptions.ALPHABETICALLY:
      return '*product_name';
    case SortOptions.DATE_CREATED:
      return 'created_at';
    case SortOptions.LAST_MODIFIED:
      return 'modified_at';
    default:
      return '*product_name';
  }
};

export const filteredProducts = (
  products: IProduct[],
  activeFilters: DefaultQueryProps,
  sort: SortOptions,
  order: 'desc' | 'asc',
) => {
  let filteredProducts = products;

  if ('name.regexp' in activeFilters) {
    filteredProducts = products.filter((product) => {
      if ('name.regexp' in activeFilters) {
        const values = activeFilters['name.regexp'];

        return filterProductByKeywords(values, product);
      }

      return false;
    });
  }

  if ('status.list' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      if ('status.list' in activeFilters) {
        const values = activeFilters['status.list'];

        return values.some(
          (value) => Boolean(toNumber(value)) === product.active,
        );
      }

      return false;
    });
  }

  if ('has_assigned_component' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      if ('has_assigned_component' in activeFilters) {
        const values = activeFilters['has_assigned_component'];

        return values.some((value) => value === product.has_assigned_component);
      }

      return false;
    });
  }

  if ('product_variation_size_id.list' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      if ('product_variation_size_id.list' in activeFilters) {
        const values = activeFilters['product_variation_size_id.list'];

        return values.some((value) =>
          product.productVariations.some(
            (item) => item.product_variation_size_id === toNumber(value),
          ),
        );
      }

      return false;
    });
  }

  if ('product_category_id' in activeFilters) {
    filteredProducts = filteredProducts.filter((product) => {
      const values = activeFilters['product_category_id'];

      return values.some((value) =>
        product.product_category_id.includes(toNumber(value)),
      );
    });
  }

  return orderBy(filteredProducts, formatSortName(sort), order);
};

/**
 * Get modified products, including deleted products and added products
 *
 * @param currentProducts current selected products
 * @param rawProducts previous selected products from endpoint
 * @param products all products' information
 * @returns modified products
 */
export const getEditedProducts = (
  currentProducts: IProduct[],
  rawProducts: IProduct[],
  products: IProduct[],
) => {
  const initialProductCodes = new Set(
    rawProducts.map((product) => product.product_code),
  );

  const currentProductCodes = new Set(
    currentProducts.map((product) => product.product_code),
  );

  const addedProductCodes = [...currentProductCodes].filter(
    (code) => !initialProductCodes.has(code),
  );

  const removedProductCodes = [...initialProductCodes].filter(
    (code) => !currentProductCodes.has(code),
  );

  const changedProductCodes = new Set([
    ...addedProductCodes,
    ...removedProductCodes,
  ]);

  const editedProducts = products.filter((product) =>
    changedProductCodes.has(product.product_code),
  );

  return editedProducts as IProduct[];
};

/**
 * Check if all products are selected
 *
 * @param products all products' information
 * @param selected selected products
 * @returns true if all products are selected, false otherwise
 */
export const areAllCategorySelected = (
  products: IProduct[],
  selected: IProduct[],
) => {
  return products.every((product) =>
    selected.some(
      (selectedProduct) =>
        selectedProduct.product_code === product.product_code,
    ),
  );
};
